import React from "react";

export const DEFAULT_CHAT_INTRO_MESSAGE = "What can I help with?";
export const DEFAULT_CHAT_INTRO_META = "";
export const DEFAULT_CHAT_PROMPTS_TITLE = "Häufige Fragen";
export const DEFAULT_CHAT_PROMPTS = [
  "Womit können Sie mir helfen?",
  "Wie funktioniert dieser Service?",
  "Welche Möglichkeiten bieten Sie?",
  "Können Sie mir Beispiele geben?",
  "Wie kann ich anfangen?",
  "Welche Funktionen sind verfügbar?",
];
export const DEFAULT_CHAT_LAUNCHER_CALL_TO_ACTION = "Lass uns reden!";
export const DEFAULT_LAUNCHER_AVATAR_SIZE = 112;
export const DEFAULT_SEARCH_PLACEHOLDER = "Search...";
export const DEFAULT_RECENTLY_VISITED_TITLE = "Recently Visited";

// Labels type definition
export type LabelsType = {
  chat: {
    inputPlaceholder: string;
    connectionLost: string;
    reloadButton: string;
    followUpsTitle: string;
    introMessage: string;
    introMessageMeta: string;
    promptSuggestionsTitle: string;
    launcherCallToAction: string;
  };
  sources: {
    searching: string;
    reading: string;
    noSourcesAvailable: string;
    sourcesCount: string;
    sourcesTitle: string;
    showAll: string;
    siteCount: string;
    siteCountPlural: string;
  };
  search: {
    startTyping: string;
    noResults: string;
    searching: string;
    placeholder: string;
    recentlyVisitedTitle: string;
  };
  thinkingBox: {
    thinking: string;
    sourcesFound: string;
    sourcesFoundPlural: string;
    moreCount: string;
    completed: string;
  };
  pdfViewer: {
    loadingError: string;
    loadingMessage: string;
    searchPlaceholder: string;
  };
  feedback: {
    thumbsUpTitle: string;
    thumbsDownTitle: string;
    thankYou: string;
  };
  landing: {
    sidebar: {
      labels: {
        chat: string;
        search: string;
        history: string;
      };
    };
  };
};

// Type-safe keys for label components
export type LabelsTypeKeys<C extends keyof LabelsType> = keyof LabelsType[C];

// Default labels
export const DEFAULT_LABELS: LabelsType = {
  chat: {
    inputPlaceholder: "Gib deine Nachricht ein...",
    connectionLost:
      "Verbindung verloren. Bitte laden Sie die Seite neu, um fortzufahren.",
    reloadButton: "Neu laden",
    followUpsTitle: "Ähnliche Themen",
    introMessage: DEFAULT_CHAT_INTRO_MESSAGE,
    introMessageMeta: DEFAULT_CHAT_INTRO_META,
    promptSuggestionsTitle: DEFAULT_CHAT_PROMPTS_TITLE,
    launcherCallToAction: DEFAULT_CHAT_LAUNCHER_CALL_TO_ACTION,
  },
  sources: {
    searching: "Searching",
    reading: "Reading",
    noSourcesAvailable: "No sources available",
    sourcesCount: "{count} Sources",
    sourcesTitle: "Sources ({count})",
    showAll: "Show all",
    siteCount: "{count} site",
    siteCountPlural: "{count} sites",
  },
  search: {
    startTyping: "Start typing to search...",
    noResults: "No results found",
    searching: "Searching...",
    placeholder: DEFAULT_SEARCH_PLACEHOLDER,
    recentlyVisitedTitle: DEFAULT_RECENTLY_VISITED_TITLE,
  },
  thinkingBox: {
    thinking: "Thinking...",
    sourcesFound: "{count} Source found",
    sourcesFoundPlural: "{count} Sources found",
    moreCount: "+{count} more",
    completed: "Completed",
  },
  pdfViewer: {
    loadingError: "Failed to load PDF document.",
    loadingMessage: "Loading PDF...",
    searchPlaceholder: "Search in PDF...",
  },
  feedback: {
    thumbsUpTitle: "Hilfreich",
    thumbsDownTitle: "Nicht hilfreich",
    thankYou: "Danke fur Ihr Feedback!",
  },
  landing: {
    sidebar: {
      labels: {
        chat: "Chat",
        search: "Search",
        history: "History",
      },
    },
  },
};

export type LinkTarget = "_blank" | "_self";

export type RegexReplace = {
  pattern: string;
  replacement: string;
};

export type PersonationContextType = {
  apiUrl: string;
  companyToken: string;
  botName: string;
  botRole: string;
  botAvatarUrl: string;
  companyMainColor: string;
  introMessage: string;
  introMessageMeta: string;
  promptSuggestionsTitle: string;
  promptSuggestions: string[];
  launcherCallToAction: string;
  launcherAvatarSize: number;
  linkTarget: LinkTarget;
  linkRegexReplaces: RegexReplace[];
  searchPlaceholder: string;
  recentlyVisitedTitle: string;
  forceTopLayer: boolean;
  labels: LabelsType;
};

const getWindowValue = (key: string, fallback = ""): string => {
  if (typeof window !== "undefined" && (window as any)[key]) {
    return (window as any)[key];
  }
  return fallback;
};

const getWindowNumber = (key: string, fallback: number): number => {
  const value = getWindowValue(key);
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getWindowList = (key: string, fallback: string[] = []): string[] => {
  const fallbackValue = fallback.length > 0 ? fallback.join("|") : "";
  const value = getWindowValue(key, fallbackValue);
  if (!value) return [];
  return value
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getWindowLinkTarget = (key: string, fallback: LinkTarget): LinkTarget => {
  if (typeof window === "undefined") {
    return fallback;
  }
  const value = (window as any)[key];
  if (value === "_blank" || value === "_self") {
    return value;
  }
  return fallback;
};

const getWindowBoolean = (key: string, fallback: boolean): boolean => {
  if (typeof window === "undefined") {
    return fallback;
  }
  const raw = (window as any)[key];
  if (typeof raw === "boolean") {
    return raw;
  }
  if (typeof raw === "string") {
    const normalized = raw.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
};

const getWindowRegexReplaces = (): RegexReplace[] => {
  if (typeof window === "undefined") {
    return [];
  }
  const value = (window as any).FIINDER_LINK_REGEX_REPLACES;
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is RegexReplace =>
        typeof item === "object" &&
        item !== null &&
        typeof item.pattern === "string" &&
        typeof item.replacement === "string",
    );
  }
  return [];
};

export const getLabelValue = <C extends keyof LabelsType>(
  component: C,
  key: LabelsTypeKeys<C>,
  legacyWindowKey: string | null,
  fallback: string,
): string => {
  // Priority 1: window.FIINDER_LABELS.{component}.{key}
  if (
    typeof window !== "undefined" &&
    (window as any).FIINDER_LABELS?.[component]?.[key]
  ) {
    return (window as any).FIINDER_LABELS[component][key];
  }
  // Priority 2: window.FIINDER_{LEGACY_KEY}
  if (legacyWindowKey) {
    const legacyValue = getWindowValue(legacyWindowKey);
    if (legacyValue) return legacyValue;
  }
  // Priority 3: fallback
  return fallback;
};

const getNestedLabelValue = (path: string[], fallback: string): string => {
  if (typeof window === "undefined") {
    return fallback;
  }
  let current: unknown = (window as any).FIINDER_LABELS;
  for (const key of path) {
    if (typeof current !== "object" || current === null) {
      return fallback;
    }
    current = (current as Record<string, unknown>)[key];
  }
  if (typeof current === "string" && current.trim()) {
    return current;
  }
  return fallback;
};

/**
 * Resolves all labels using the 3-tier priority system:
 * 1. window.FIINDER_LABELS.{component}.{key} (highest priority)
 * 2. Legacy window.FIINDER_* variables (for backward compatibility, where applicable)
 * 3. Built-in defaults
 */
export function resolveLabels(): LabelsType {
  return {
    chat: {
      inputPlaceholder: getLabelValue(
        "chat",
        "inputPlaceholder",
        "FIINDER_CHAT_INPUT_PLACEHOLDER",
        DEFAULT_LABELS.chat.inputPlaceholder,
      ),
      connectionLost: getLabelValue(
        "chat",
        "connectionLost",
        null,
        DEFAULT_LABELS.chat.connectionLost,
      ),
      reloadButton: getLabelValue(
        "chat",
        "reloadButton",
        null,
        DEFAULT_LABELS.chat.reloadButton,
      ),
      followUpsTitle: getLabelValue(
        "chat",
        "followUpsTitle",
        null,
        DEFAULT_LABELS.chat.followUpsTitle,
      ),
      introMessage: getLabelValue(
        "chat",
        "introMessage",
        "FIINDER_CHAT_INTRO_MESSAGE",
        DEFAULT_LABELS.chat.introMessage,
      ),
      introMessageMeta: getLabelValue(
        "chat",
        "introMessageMeta",
        "FIINDER_CHAT_INTRO_META",
        DEFAULT_LABELS.chat.introMessageMeta,
      ),
      promptSuggestionsTitle: getLabelValue(
        "chat",
        "promptSuggestionsTitle",
        "FIINDER_CHAT_START_QUICK_QUESTIONS_TITLE",
        DEFAULT_LABELS.chat.promptSuggestionsTitle,
      ),
      launcherCallToAction: getLabelValue(
        "chat",
        "launcherCallToAction",
        "FIINDER_CHAT_LAUNCHER_CALL_TO_ACTION",
        DEFAULT_LABELS.chat.launcherCallToAction,
      ),
    },
    sources: {
      searching: getLabelValue(
        "sources",
        "searching",
        null,
        DEFAULT_LABELS.sources.searching,
      ),
      reading: getLabelValue(
        "sources",
        "reading",
        null,
        DEFAULT_LABELS.sources.reading,
      ),
      noSourcesAvailable: getLabelValue(
        "sources",
        "noSourcesAvailable",
        null,
        DEFAULT_LABELS.sources.noSourcesAvailable,
      ),
      sourcesCount: getLabelValue(
        "sources",
        "sourcesCount",
        null,
        DEFAULT_LABELS.sources.sourcesCount,
      ),
      sourcesTitle: getLabelValue(
        "sources",
        "sourcesTitle",
        null,
        DEFAULT_LABELS.sources.sourcesTitle,
      ),
      showAll: getLabelValue(
        "sources",
        "showAll",
        null,
        DEFAULT_LABELS.sources.showAll,
      ),
      siteCount: getLabelValue(
        "sources",
        "siteCount",
        null,
        DEFAULT_LABELS.sources.siteCount,
      ),
      siteCountPlural: getLabelValue(
        "sources",
        "siteCountPlural",
        null,
        DEFAULT_LABELS.sources.siteCountPlural,
      ),
    },
    search: {
      startTyping: getLabelValue(
        "search",
        "startTyping",
        null,
        DEFAULT_LABELS.search.startTyping,
      ),
      noResults: getLabelValue(
        "search",
        "noResults",
        null,
        DEFAULT_LABELS.search.noResults,
      ),
      searching: getLabelValue(
        "search",
        "searching",
        null,
        DEFAULT_LABELS.search.searching,
      ),
      placeholder: getLabelValue(
        "search",
        "placeholder",
        "FIINDER_SEARCH_PLACEHOLDER",
        DEFAULT_LABELS.search.placeholder,
      ),
      recentlyVisitedTitle: getLabelValue(
        "search",
        "recentlyVisitedTitle",
        "FIINDER_RECENTLY_VISITED_TITLE",
        DEFAULT_LABELS.search.recentlyVisitedTitle,
      ),
    },
    thinkingBox: {
      thinking: getLabelValue(
        "thinkingBox",
        "thinking",
        null,
        DEFAULT_LABELS.thinkingBox.thinking,
      ),
      sourcesFound: getLabelValue(
        "thinkingBox",
        "sourcesFound",
        null,
        DEFAULT_LABELS.thinkingBox.sourcesFound,
      ),
      sourcesFoundPlural: getLabelValue(
        "thinkingBox",
        "sourcesFoundPlural",
        null,
        DEFAULT_LABELS.thinkingBox.sourcesFoundPlural,
      ),
      moreCount: getLabelValue(
        "thinkingBox",
        "moreCount",
        null,
        DEFAULT_LABELS.thinkingBox.moreCount,
      ),
      completed: getLabelValue(
        "thinkingBox",
        "completed",
        null,
        DEFAULT_LABELS.thinkingBox.completed,
      ),
    },
    pdfViewer: {
      loadingError: getLabelValue(
        "pdfViewer",
        "loadingError",
        null,
        DEFAULT_LABELS.pdfViewer.loadingError,
      ),
      loadingMessage: getLabelValue(
        "pdfViewer",
        "loadingMessage",
        null,
        DEFAULT_LABELS.pdfViewer.loadingMessage,
      ),
      searchPlaceholder: getLabelValue(
        "pdfViewer",
        "searchPlaceholder",
        null,
        DEFAULT_LABELS.pdfViewer.searchPlaceholder,
      ),
    },
    feedback: {
      thumbsUpTitle: getLabelValue(
        "feedback",
        "thumbsUpTitle",
        null,
        DEFAULT_LABELS.feedback.thumbsUpTitle,
      ),
      thumbsDownTitle: getLabelValue(
        "feedback",
        "thumbsDownTitle",
        null,
        DEFAULT_LABELS.feedback.thumbsDownTitle,
      ),
      thankYou: getLabelValue(
        "feedback",
        "thankYou",
        null,
        DEFAULT_LABELS.feedback.thankYou,
      ),
    },
    landing: {
      sidebar: {
        labels: {
          chat: getNestedLabelValue(
            ["landing", "sidebar", "labels", "chat"],
            DEFAULT_LABELS.landing.sidebar.labels.chat,
          ),
          search: getNestedLabelValue(
            ["landing", "sidebar", "labels", "search"],
            DEFAULT_LABELS.landing.sidebar.labels.search,
          ),
          history: getNestedLabelValue(
            ["landing", "sidebar", "labels", "history"],
            DEFAULT_LABELS.landing.sidebar.labels.history,
          ),
        },
      },
    },
  };
}

export const defaultPersonationContext: PersonationContextType = {
  apiUrl: getWindowValue("FIINDER_API_URL"),
  companyToken: getWindowValue("FIINDER_COMPANY_TOKEN"),
  botName: getWindowValue("FIINDER_AGENT_NAME", "Bot"),
  botRole: getWindowValue("FIINDER_AGENT_ROLE", "Assistant"),
  botAvatarUrl: getWindowValue("FIINDER_AGENT_AVATAR_URL", ""),
  companyMainColor: getWindowValue("FIINDER_COMPANY_MAIN_COLOR", "#e6001e"),
  introMessage: getWindowValue(
    "FIINDER_CHAT_INTRO_MESSAGE",
    DEFAULT_CHAT_INTRO_MESSAGE,
  ),
  introMessageMeta: getWindowValue(
    "FIINDER_CHAT_INTRO_META",
    DEFAULT_CHAT_INTRO_META,
  ),
  promptSuggestionsTitle: getWindowValue(
    "FIINDER_CHAT_START_QUICK_QUESTIONS_TITLE",
    DEFAULT_CHAT_PROMPTS_TITLE,
  ),
  promptSuggestions: getWindowList(
    "FIINDER_CHAT_START_QUICK_QUESTIONS",
    DEFAULT_CHAT_PROMPTS,
  ),
  launcherCallToAction: getWindowValue(
    "FIINDER_CHAT_LAUNCHER_CALL_TO_ACTION",
    DEFAULT_CHAT_LAUNCHER_CALL_TO_ACTION,
  ),
  launcherAvatarSize: getWindowNumber(
    "FIINDER_AVATAR_SIZE",
    DEFAULT_LAUNCHER_AVATAR_SIZE,
  ),
  linkTarget: getWindowLinkTarget("FIINDER_LINK_TARGET", "_self"),
  linkRegexReplaces: getWindowRegexReplaces(),
  searchPlaceholder: getWindowValue(
    "FIINDER_SEARCH_PLACEHOLDER",
    DEFAULT_SEARCH_PLACEHOLDER,
  ),
  recentlyVisitedTitle: getWindowValue(
    "FIINDER_RECENTLY_VISITED_TITLE",
    DEFAULT_RECENTLY_VISITED_TITLE,
  ),
  forceTopLayer: getWindowBoolean("FIINDER_FORCE_TOP_LAYER", true),
  labels: resolveLabels(),
};

export const PersonationContext = React.createContext<PersonationContextType>(
  defaultPersonationContext,
);

PersonationContext.displayName = "PersonationContext";

export const usePersonation = () => React.useContext(PersonationContext);
